import React, { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getModerationUser, liftModerationSpeechChatRestriction, setModerationSpeechChatRestriction } from '../../api/authAdmin';
import { t } from '../../utils/i18n';
import { getSelectedEnvironment } from '../../utils/apiEnvironment';
import { formatDateTimeKST } from '../../utils/kstDateTime';
import { speechErrorMessage } from '../../utils/speechModeration';

export default function UserChatRestrictionDialog({ user, token, apiEnvironment, onClose }) {
  const userId = user.user_id ?? user.UserID;
  const client = useQueryClient();
  const panel = useRef(null);
  const busy = useRef(false);
  const mounted = useRef(true);
  const [duration, setDuration] = useState('10m');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [confirmation, setConfirmation] = useState(null);
  const [result, setResult] = useState(null);
  const [now, setNow] = useState(Date.now());
  const query = useQuery({
    queryKey: ['user-chat-restriction', apiEnvironment, userId],
    queryFn: async () => {
      const response = await getModerationUser(userId, token, { includeUGC: true, contentType: 'SPEECH', page: 1, pageSize: 1 });
      if (!Array.isArray(response?.ugc?.chat_restrictions)) throw new Error(t('user.chatRestriction.unavailable'));
      return response.ugc;
    },
    staleTime: 0,
    refetchOnMount: 'always',
    retry: false,
  });
  const active = (query.data?.chat_restrictions ?? [])
    .filter((item) => !item.lifted_at && Date.parse(item.expires_at) > now)
    .sort((a, b) => b.duration_seconds - a.duration_seconds || Date.parse(b.expires_at) - Date.parse(a.expires_at))[0];
  const incomplete = query.data?.restriction_history_truncated === true;
  const mutation = useMutation({
    mutationFn: (action) => {
      if (!mounted.current || getSelectedEnvironment() !== apiEnvironment) throw new Error(t('user.chatRestriction.environmentChanged'));
      return action.kind === 'lift'
        ? liftModerationSpeechChatRestriction(userId, action.input, token)
        : setModerationSpeechChatRestriction(userId, action.duration, action.input, token);
    },
    onSuccess: async (response) => {
      if (!mounted.current) return;
      setResult({ success: true, auditId: response.audit_id });
      setConfirmation(null);
      await Promise.all([
        query.refetch(),
        client.invalidateQueries({ queryKey: ['moderation-speech-cases', apiEnvironment] }),
        client.invalidateQueries({ queryKey: ['moderation-speech-case', apiEnvironment] }),
        client.invalidateQueries({ queryKey: ['moderation-user', apiEnvironment, userId] }),
        client.invalidateQueries({ queryKey: ['moderation-user-ugc', apiEnvironment, userId] }),
        client.invalidateQueries({ queryKey: ['user-detail-ugc', apiEnvironment] }),
      ]);
    },
    onError: async (error) => {
      if (!mounted.current) return;
      setResult({ error: speechErrorMessage(error) });
      if (error.status === 409) {
        setConfirmation(null);
        await query.refetch();
      }
    },
    onSettled: () => { busy.current = false; },
  });
  useEffect(() => {
    mounted.current = true;
    const previous = document.activeElement;
    panel.current?.querySelector('button')?.focus();
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => { mounted.current = false; clearInterval(timer); previous?.focus?.(); };
  }, []);
  const prepare = (kind) => {
    setResult(null);
    setConfirmation({ kind, duration, input: {
      reason_code: reason.trim(), notes: notes.trim() || null, request_id: crypto.randomUUID(),
      ...(kind === 'lift' ? { restriction_id: active.restriction_id } : {}),
    } });
  };
  const submit = () => {
    if (busy.current || !confirmation) return;
    busy.current = true;
    mutation.mutate(confirmation);
  };
  const ready = !query.isFetching && !query.isError && !!query.data && !incomplete;
  const disabled = mutation.isPending;
  const onKeyDown = (event) => {
    if (event.key === 'Escape' && !busy.current) onClose();
    if (event.key !== 'Tab') return;
    const items = [...panel.current.querySelectorAll('button:not(:disabled), input:not(:disabled), textarea:not(:disabled), select:not(:disabled)')];
    const first = items[0]; const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
    if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
  };
  return <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/60 p-4" onClick={() => { if (!busy.current) onClose(); }}>
    <section ref={panel} role="dialog" aria-modal="true" aria-labelledby="chat-restriction-title" onKeyDown={onKeyDown} onClick={(event) => event.stopPropagation()} className="max-h-[90vh] w-full max-w-lg space-y-4 overflow-y-auto rounded-xl border border-dark-border bg-dark-card p-5 text-dark-text shadow-xl">
      <div className="flex items-center justify-between"><h2 id="chat-restriction-title" className="text-lg font-semibold">{t('user.chatRestriction.title')}</h2><button type="button" disabled={disabled} onClick={onClose}>{t('user.chatRestriction.close')}</button></div>
      <p className="rounded-lg border border-dark-border p-3 text-sm">{apiEnvironment.toUpperCase()} · #{userId} · {user.username ?? user.Username ?? ''}<br />{user.name ?? user.Name ?? ''}</p>
      <p className="text-sm text-dark-text-secondary">{t('user.chatRestriction.boundary')}</p>
      <div className="rounded-lg bg-dark-surface p-3 text-sm">
        <p className="font-medium">{t('user.chatRestriction.manualStatus')}</p>
        {query.isPending ? <p>{t('common.loading')}</p> : query.isError ? <p role="alert">{speechErrorMessage(query.error)}</p> : <p>{active ? t('user.chatRestriction.until', { time: formatDateTimeKST(active.expires_at) }) : incomplete ? t('user.chatRestriction.incomplete') : t('user.chatRestriction.none')}</p>}
        {active && <p>{t('user.chatRestriction.reason')}: {active.reason_code}</p>}
        {incomplete && active && <p>{t('user.chatRestriction.incomplete')}</p>}
        <button type="button" disabled={disabled || query.isFetching} onClick={() => { setConfirmation(null); query.refetch(); }} className="mt-2 text-primary">{t('user.chatRestriction.refresh')}</button>
      </div>
      {!confirmation ? <>
        <label className="block text-sm">{t('user.chatRestriction.duration')}<select value={duration} disabled={disabled || !!active} onChange={(event) => setDuration(event.target.value)} className="mt-1 w-full rounded border border-dark-border bg-dark-surface p-2">{['10m', '1h', '24h'].map((value) => <option key={value} value={value}>{t(`user.chatRestriction.durations.${value}`)}</option>)}</select></label>
        <label className="block text-sm">{t('user.chatRestriction.reason')}<input value={reason} maxLength={64} disabled={disabled} onChange={(event) => setReason(event.target.value)} className="mt-1 w-full rounded border border-dark-border bg-dark-surface p-2" /></label>
        <label className="block text-sm">{t('user.chatRestriction.notes')}<textarea value={notes} maxLength={1000} disabled={disabled} onChange={(event) => setNotes(event.target.value)} className="mt-1 w-full rounded border border-dark-border bg-dark-surface p-2" /></label>
        <p className="text-xs text-dark-text-secondary">{t('user.chatRestriction.automaticBoundary')}</p>
        <div className="flex justify-end gap-2"><button type="button" disabled={!ready || disabled || !!active || !reason.trim()} onClick={() => prepare('apply')} className="rounded bg-amber-700 px-4 py-2 disabled:opacity-40">{t('user.chatRestriction.apply')}</button><button type="button" disabled={!ready || disabled || !active?.restriction_id || !reason.trim()} onClick={() => prepare('lift')} className="rounded bg-sky-700 px-4 py-2 disabled:opacity-40">{t('user.chatRestriction.lift')}</button></div>
      </> : <>
        <p className="whitespace-pre-wrap rounded border border-amber-400/30 p-3 text-sm">{t(confirmation.kind === 'lift' ? 'user.chatRestriction.confirmLift' : 'user.chatRestriction.confirmApply', { duration: t(`user.chatRestriction.durations.${confirmation.duration}`), environment: apiEnvironment.toUpperCase(), userId })}<br />{confirmation.input.reason_code}{confirmation.input.notes ? `\n${confirmation.input.notes}` : ''}</p>
        <div className="flex justify-end gap-2"><button type="button" disabled={disabled} onClick={() => { setConfirmation(null); setResult(null); }}>{t('common.cancel')}</button><button type="button" disabled={disabled} onClick={submit} className="rounded bg-primary px-4 py-2 text-black disabled:opacity-40">{disabled ? t('user.ban.processing') : t('common.confirm')}</button></div>
      </>}
      {result?.success && <p role="status" className="text-green-400">{t('user.chatRestriction.success', { auditId: result.auditId ?? '—' })}</p>}
      {result?.error && <p role="alert" className="text-red-400">{result.error}</p>}
    </section>
  </div>;
}
